import { useEffect, useLayoutEffect, useRef, useState } from "react"
import type { ReactNode, RefObject } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { TooltipProvider } from "@nexus/ui"

// Matches the grid's Tailwind breakpoints: `grid-cols-2 sm:grid-cols-4 xl:grid-cols-6`.
// This page is a full-width dashboard view (not a narrow drawer like Opportunities'
// VirtualizedCreativeGrid, which this component's virtualization logic is adapted
// from), so it earns a denser top tier: 6 smaller cards per row on wide desktop
// viewports, stepping down to 4 on tablet and 2 on mobile.
const SM_BREAKPOINT_PX = 640
const XL_BREAKPOINT_PX = 1280
const COLUMNS_BASE = 2
const COLUMNS_SM = 4
const COLUMNS_XL = 6

// Row height is refined per-row by measureElement; this estimate only seeds the
// initial scrollbar size. A card is an aspect-[4/5] thumbnail + stats strip.
const ESTIMATED_ROW_PX = 340
const ROW_OVERSCAN = 3

/**
 * Tracks the active column count, mirroring the grid's
 * `grid-cols-2 sm:grid-cols-4 xl:grid-cols-6` breakpoints.
 */
function useColumnCount(): number {
  const resolveCols = () => {
    if (typeof window === "undefined") return COLUMNS_BASE
    if (window.innerWidth >= XL_BREAKPOINT_PX) return COLUMNS_XL
    if (window.innerWidth >= SM_BREAKPOINT_PX) return COLUMNS_SM
    return COLUMNS_BASE
  }

  const [cols, setCols] = useState(resolveCols)

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const smQuery = window.matchMedia(`(min-width: ${SM_BREAKPOINT_PX}px)`)
    const xlQuery = window.matchMedia(`(min-width: ${XL_BREAKPOINT_PX}px)`)
    const update = () => setCols(resolveCols())
    update()
    smQuery.addEventListener("change", update)
    xlQuery.addEventListener("change", update)
    return () => {
      smQuery.removeEventListener("change", update)
      xlQuery.removeEventListener("change", update)
    }
  }, [])

  return cols
}

export interface DrilldownVirtualizedGridProps<T> {
  items: T[]
  emptyLabel: string
  /**
   * The page's scroll container. When provided the grid virtualizes against
   * it; when absent (Storybook, isolated tests) it falls back to a plain grid
   * so off-context usage stays unchanged.
   */
  scrollContainerRef?: RefObject<HTMLElement | null>
  getKey: (item: T) => string
  renderItem: (item: T) => ReactNode
}

function PlainGrid<T>({
  items,
  getKey,
  renderItem,
}: {
  items: T[]
  getKey: (item: T) => string
  renderItem: (item: T) => ReactNode
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
        {items.map((item) => (
          <div key={getKey(item)}>{renderItem(item)}</div>
        ))}
      </div>
    </TooltipProvider>
  )
}

/**
 * Row-virtualized Drilldown grid. Generic over item type and rendering so it
 * has NO dependency on the Opportunities feature's `CreativeExample`/
 * `CreativeCardContext` — this is what guarantees the shared Opportunities
 * `VirtualizedCreativeGrid`/`CreativeCard` are never touched by this redesign.
 * Virtualization approach (column tracking, scroll-margin measurement, rAF
 * re-measure workaround) is adapted from that component's proven logic.
 */
export function DrilldownVirtualizedGrid<T>({
  items,
  emptyLabel,
  scrollContainerRef,
  getKey,
  renderItem,
}: DrilldownVirtualizedGridProps<T>) {
  const cols = useColumnCount()
  const gridRef = useRef<HTMLDivElement>(null)
  const rowCount = Math.ceil(items.length / cols)

  // The grid sits below the toolbar/filters sections inside the shared page
  // scroll container, so the virtualizer needs to know that offset to map
  // scroll position onto the correct rows.
  const [scrollMargin, setScrollMargin] = useState(0)
  useLayoutEffect(() => {
    const grid = gridRef.current
    const scroller = scrollContainerRef?.current
    if (!grid || !scroller) return

    const measure = () => {
      const offset =
        grid.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop
      setScrollMargin(offset)
    }
    measure()

    const ro = new ResizeObserver(measure)
    ro.observe(scroller)
    ro.observe(grid)
    return () => ro.disconnect()
  }, [scrollContainerRef, items.length, cols])

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollContainerRef?.current ?? null,
    estimateSize: () => ESTIMATED_ROW_PX,
    overscan: ROW_OVERSCAN,
    scrollMargin,
  })

  // When the grid mounts in the same commit as a freshly-created scroll
  // container, the virtualizer can latch a 0×0 viewport rect before flex
  // layout has resolved the container's real height. Re-measure once after
  // layout settles so the real viewport size is picked up.
  useLayoutEffect(() => {
    if (!scrollContainerRef) return
    const id = requestAnimationFrame(() => virtualizer.measure())
    return () => cancelAnimationFrame(id)
  }, [virtualizer, scrollContainerRef])

  if (items.length === 0) {
    return <p className="py-6 text-center text-xs text-muted-foreground">{emptyLabel}</p>
  }

  // No scroll container in context (stories / isolated tests): render everything.
  if (!scrollContainerRef) {
    return <PlainGrid items={items} getKey={getKey} renderItem={renderItem} />
  }

  const virtualRows = virtualizer.getVirtualItems()

  return (
    <TooltipProvider delayDuration={300}>
      <div
        ref={gridRef}
        style={{ height: virtualizer.getTotalSize(), position: "relative", width: "100%" }}
      >
        {virtualRows.map((virtualRow) => {
          const start = virtualRow.index * cols
          const rowItems = items.slice(start, start + cols)
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
              }}
            >
              {/* pb-3 carries the gap-3 row spacing into the measured row height */}
              <div className="grid grid-cols-2 gap-3 pb-3 sm:grid-cols-4 xl:grid-cols-6">
                {rowItems.map((item) => (
                  <div key={getKey(item)}>{renderItem(item)}</div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </TooltipProvider>
  )
}
